import { defineStore } from 'pinia'
import { object, string, number } from 'yup'
import type { Schema } from 'yup'
import type { CalendarSlots } from '@/prisma/client'

type SlotSchemaKeys = 'name' | 'quantity' | 'timeStart' | 'timeEnd'

export function slotSchema(fields: SlotSchemaKeys[], t: Function) {
  const baseSchema = {
    name: string().required(t('errors.required')),
    quantity: number()
      .required(t('errors.required'))
      .min(1, t('errors.minValue', { min: 1 }))
      .integer(t('errors.integer')),
    timeStart: string().required(t('errors.required')),
    timeEnd: string().required(t('errors.required'))
      .test('end-after-start', t('errors.endAfterStart'), function (value) {
        if (!value || !this.parent.timeStart) return true
        return value > this.parent.timeStart
      }),
  }

  const partialSchema: Partial<Record<SlotSchemaKeys, Schema<any>>> = {}
  fields.forEach((fieldName) => {
    if (baseSchema[fieldName]) {
      partialSchema[fieldName] = baseSchema[fieldName]
    }
  })
  return object(partialSchema)
}

export const useSlotStore = defineStore('slot', () => {
  const slots = ref<CalendarSlots[]>([])

  async function createSlot(calendarId: number, slot: Partial<CalendarSlots>) {
    try {
      const result = await $fetch<CalendarSlots | null>(`/api/calendar/${calendarId}/slot`, {
        method: 'POST',
        body: slot,
      })

      if (result) {
        slots.value = [...(slots.value || []), result]
      }

      return { data: result, status: 'success', error: null }
    } catch (e: any) {
      return { data: null, status: 'error', error: e.data || e }
    }
  }

  async function fetchSlots(calendarId: number) {
    try {
      const result = await $fetch<CalendarSlots[] | null>(`/api/calendar/${calendarId}/slot`)

      if (result) {
        slots.value = result
      }

      return { data: slots.value, status: 'success', error: null }
    } catch (e: any) {
      return { data: slots.value, status: 'error', error: e.data || e }
    }
  }

  async function updateSlot(calendarId: number, slotId: number, slot: Partial<CalendarSlots>) {
    try {
      const result = await $fetch<CalendarSlots | null>(`/api/slot/${slotId}`, {
        method: 'PUT',
        body: slot,
      })

      if (result) {
        const index = slots.value?.findIndex(s => s.id === slotId)
        if (index !== -1) {
          slots.value[index] = result
        }
      }

      return { data: result, status: 'success', error: null }
    } catch (e: any) {
      return { data: null, status: 'error', error: e.data || e }
    }
  }

  async function deleteSlot(slotId: number) {
    try {
      const result = await $fetch<CalendarSlots | null>(`/api/slot/${slotId}`, {
        method: 'DELETE',
      })

      if (result) {
        const index = slots.value?.findIndex(s => s.id === slotId)
        if (index !== -1) {
          slots.value?.splice(index, 1)
        }
      }

      return { data: result, status: 'success', error: null }
    } catch (e: any) {
      return { data: null, status: 'error', error: e.data || e }
    }
  }

  return {
    createSlot,
    updateSlot,
    fetchSlots,
    deleteSlot,
    slots,
  }
})
